<template>
  <div>
    <div ref="alertContainer" v-if="!!alertMsg">
      <b-alert
        :show="!!alertMsg"
        :variant="alertMsg.type"
        dismissible
        v-on:dismissed="alertMsg = null"
      >
        <h4 class="alert-heading">
          {{ alertMsg.type === 'danger' ? 'Error!' : 'Success!' }}
        </h4>
        <hr />
        <p class="font-weight-bold">{{ alertMsg.message }}</p>
      </b-alert>
    </div>
    <v-wait :for="loaderId">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">
          Loading the members...
        </div>
      </template>
      <DataTable
        :fields="fields"
        :filters="filters"
        :items="filteredMembers"
        :no-local-sorting="false"
        :show-filters="true"
        :show-pager="true"
        :table-class="'table-responsive'"
        ref="dataTable"
      >
        <!-- template for filters -->
        <template slot="filters">
          <div class="p-3">
            <b-row>
              <b-col sm="6" md="6" lg="6" xl="3">
                <label class="phairify-color-teal body-copy-font">
                  <strong>Status:</strong>
                </label>
                <div class="phairify-select-wrapper">
                  <b-form-select
                    class="rounded-0 phairify-border-grey"
                    :options="statusOptions"
                    v-model="filters.status.value"
                  />
                </div>
              </b-col>
              <b-col sm="6" md="6" lg="6" xl="3" class="mt-2 mt-sm-0">
                <label class="phairify-color-teal body-copy-font">
                  <strong>Email:</strong>
                </label>
                <b-form-input
                  placeholder="Email filter"
                  v-model="filters.email.value"
                />
              </b-col>
              <b-col sm="6" md="6" lg="6" xl="3" class="mt-2 mt-xl-0">
                <label class="phairify-color-teal body-copy-font">
                  <strong>Primary Study Status:</strong>
                </label>
                <div class="phairify-select-wrapper">
                  <b-form-select
                    class="rounded-0 phairify-border-grey"
                    :options="studyStatusOptions"
                    v-model="filters.studyStatus.value"
                  />
                </div>
              </b-col>
              <b-col sm="6" md="6" lg="6" xl="3" class="d-flex align-items-end justify-content-center">
                <b-button variant="v2_primary" @click="onGenerateCSV">Download</b-button>
              </b-col>
            </b-row>
          </div>
        </template>
        <!-- templates for custom columns -->
        <template slot="status" slot-scope="data">
          <div>{{ mapStatusToTitle[data.item.status] }}</div>
          <div v-if="mapStatusToTitle[data.item.status] === 'Disabled'">
            <b-button @click="onEnableDisable(data.item)" variant="info">
              <template v-if="!enableLoading">Enable</template>
              <template v-else>
                <b-spinner small />
                <span>Loading...</span>
              </template>
            </b-button>
          </div>
        </template>
        <template slot="send_reg_email_date" slot-scope="data">
          <div v-if="data.item.number_of_links_sent !== 0">
            <span>
              {{ $dayjs(data.item.send_reg_email_date).format('MM/DD/YYYY') }}
            </span>
            <span>{{ ' ' }}</span>
            <span>{{ `[${data.item.number_of_links_sent}]` }}</span>
          </div>
          <div
            v-if="
              isGmAdmin && mapStatusToTitle[data.item.status] === 'Unregistered'
            "
          >
            <b-button @click="onSend(data.item)" size="sm" variant="info">
              <template
                v-if="sendEmailMemberId && sendEmailMemberId === data.item.id"
              >
                <b-spinner small />
              </template>
              <template v-else>Send Email</template>
            </b-button>
          </div>
        </template>
        <template slot="actions" slot-scope="data">
          <div
            class="d-flex align-items-center justify-content-center flex-column"
          >
            <b-button
              v-if="
                mapStatusToTitle[data.item.status] === 'Active' && isMSSAdmin
              "
              size="sm"
              variant="default"
              @click="onEditMember(data.item)"
            >
              <i class="mdi mdi-lead-pencil" />
            </b-button>
            <b-button
              v-if="
                mapStatusToTitle[data.item.status] !== 'Active' && isGmAdmin
              "
              size="sm"
              variant="info"
              @click="onShowLink(data.item)"
            >
              <template v-if="loadingRegTokenButtonId !== data.item.id">
                Show Link
              </template>
              <template v-else>
                <b-spinner small />
              </template>
            </b-button>
          </div>
        </template>
      </DataTable>
    </v-wait>

    <user-edit-modal
      v-if="showEditModal"
      :member-id="editedMember.id"
      @modalCancel="onModalCancel"
      @userSaved="onUserSaved"
    />
    <registration-token-modal
      :token="registrationToken"
      :visible="isRegistrationModalVisible"
      :title="registrationTokenModalTitle"
      @modal-close="isRegistrationModalVisible = false"
    />
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import Api from '@/providers/Api.js';
import { HalfCircleSpinner } from 'epic-spinners';
import Help from '@/providers/Help.js';
import UserEditModal from '@/components/society/UserEditModal';
import RegistrationTokenModal from '@/components/society/edit-members/RegistrationTokenModal';
import {csvEscape} from "@/helpers/csvHelper";

const MEMBER_STATUS_DISABLED = 0;
const MEMBER_STATUS_ACTIVE = 1;
const MEMBER_STATUS_UNREGISTERED = 2;

const STUDY_STATUSES = {
  ANY: null,
  COMPLETED: 'completed',
  NOT_STARTED: 'not_started',
  NOT_COMPLETED: 'not_completed',
};

const STUDY_STATUS_LABELS = {
  COMPLETED: 'Completed',
  NOT_STARTED: 'Not Started',
  NOT_COMPLETED: 'Started - Not Completed',
};

export default {
  name: 'MembersList',
  components: {
    DataTable,
    HalfCircleSpinner,
    UserEditModal,
    RegistrationTokenModal,
  },
  props: {
    societyId: {
      type: [Number, String],
      required: true,
    },
  },
  data: function () {
    const data = {
      members: [],
      filters: {
        email: { field: 'email', type: 'string', value: null },
        status: { field: 'status', type: 'string', value: null },
        studyStatus: { field: 'studyStatus', type: 'string', value: null}
      },
      fields: [
        { key: 'status', sortable: true, custom: true },
        { key: 'email', sortable: true },
        { key: 'society_member_uid', label: 'Society ID' },
        { key: 'zip_code' },
        { key: 'specialty', label: 'Specialty' },
        { key: 'subspecialty', label: 'Subspecialty' },
        {
          key: 'user',
          formatter: (val) => this.studyStatusToText(val),
          label: 'Primary Study Status',
        },
        {
          key: 'gender',
          formatter: (val) => this.optionsKeyToText(val, 'genderOptions'),
        },
        { key: 'member_type', label: 'Member Type' },
        { key: 'send_reg_email_date', label: 'Registration', custom: true },
        { key: 'actions', label: 'Actions', custom: true },
      ],
      alertMsg: null,
      sendEmailMemberId: null,
      enableLoading: false,
      showEditModal: false,
      editedMember: null,
      registrationToken: null,
      registrationTokenModalTitle: '',
      isRegistrationModalVisible: false,
      loadingRegTokenButtonId: null,
    };

    if (this.$store.getters.userIsGMAdmin) {
      data.fields.splice(2, 0, { key: 'is_demo', label: 'Demo' });
    }

    return data;
  },
  created: function () {
    this.refreshData();
  },
  computed: {
    filteredMembers: function () {
      const filterByEmail = this.filters.email && !!this.filters.email.value;
      const filterByStatus = this.filters.status && !!this.filters.status.value;
      const filterByStudyStatus = this.filters.studyStatus && !!this.filters.studyStatus.value;
      if (filterByEmail || filterByStatus || filterByStudyStatus) {
        return this.members.filter((m) => {
          let isMatch = true;

          if (filterByEmail) {
            isMatch &= m.email.includes(this.filters.email.value);
          }

          if (filterByStatus) {
            isMatch &=
              parseInt(m.status) === parseInt(this.filters.status.value);
          }

          if (filterByStudyStatus) {
            switch (this.filters.studyStatus.value) {
              case STUDY_STATUSES.COMPLETED:
                isMatch &= m.user && m.user.primaryActiveSurvey && !!m.user.primaryActiveSurvey.submitted;
                break;
              case STUDY_STATUSES.NOT_COMPLETED:
                isMatch &= m.user && m.user.primaryActiveSurvey && !m.user.primaryActiveSurvey.submitted;
                break;
              case STUDY_STATUSES.NOT_STARTED:
                isMatch &= m.user && !m.user.primaryActiveSurvey;
                break;
            }
          }
          return isMatch;
        });
      }
      return this.members;
    },
    mapStatusToTitle: function () {
      return {
        [MEMBER_STATUS_DISABLED]: 'Disabled',
        [MEMBER_STATUS_ACTIVE]: 'Active',
        [MEMBER_STATUS_UNREGISTERED]: 'Unregistered',
      };
    },
    statusOptions: function () {
      return [
        {
          value: null,
          text: 'All',
        },
        ...Object.keys(this.mapStatusToTitle).map((status) => ({
          value: status,
          text: this.mapStatusToTitle[status],
        })),
      ];
    },
    studyStatusOptions: function() {
      return [
        {
          value: STUDY_STATUSES.ANY,
          text: 'Any status',
        },
        {
          value: STUDY_STATUSES.COMPLETED,
          text: 'Completed',
        },
        {
          value: STUDY_STATUSES.NOT_STARTED,
          text: 'Not Started',
        },
        {
          value: STUDY_STATUSES.NOT_COMPLETED,
          text: 'Started - Not Completed',
        },
      ];
    },
    isGmAdmin: function () {
      return this.$store.getters.userIsGMAdmin;
    },
    isMSSAdmin: function () {
      return this.$store.getters.userIsMSSAdmin;
    },
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
  },
  methods: {
    optionsKeyToText: function (val, stateKey) {
      if (!val) return null;
      const options = this.$store.state[stateKey];
      const optObj = options.find(({ value }) => value === val);
      return (optObj || {}).text;
    },
    studyStatusToText: function (user) {
      if (!user) {
        return '-'
      } else if (!user.primaryActiveSurvey) {
        return STUDY_STATUS_LABELS.NOT_STARTED;
      } else if (user.primaryActiveSurvey && !user.primaryActiveSurvey.submitted) {
        return STUDY_STATUS_LABELS.NOT_COMPLETED;
      } else if (user.primaryActiveSurvey && !!user.primaryActiveSurvey.submitted) {
        return STUDY_STATUS_LABELS.COMPLETED;
      }
      return '-';
    },
    refreshData: async function () {
      this.$wait.start(this.loaderId);
      try {
        this.members = await Api.getAllSocietyMembers(this.societyId);
      } catch (error) {
        this.$toasted.show('Upload data error', {
          type: 'error',
          duration: 3000,
        });
      }
      this.$wait.end(this.loaderId);
    },
    sendInvitation: async function (member) {
      this.sendEmailMemberId = member.id;
      try {
        const {
          success = false,
          memberData = null,
        } = await Api.sendIndividualInvitation(member.id);
        if (success) {
          this.alertMsg = {
            type: 'success',
            message: `Successfully sent email to ${member.email}`,
          };
          if (memberData) {
            const memberIndex = this.members.findIndex(
              (m) => m.id === memberData.id
            );
            if (memberIndex > -1)
              this.$set(this.members, memberIndex, memberData);
          }
          this.sendEmailMemberId = null;
          this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
        }
      } catch (error) {
        this.alertMsg = {
          type: 'danger',
          message:
            error && error.response && error.response.data
              ? error.response.data.message
              : 'Server error',
        };
        this.sendEmailMemberId = null;
        this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
      }
    },
    onSend: async function (member) {
      Help.confirm.show(
        'Are you sure that you want to send follow up registration email to this member?',
        'Send',
        () => {
          this.sendInvitation(member);
        }
      );
    },
    enableDisable: async function (member) {
      this.enableLoading = true;
      try {
        const {
          success = false,
          memberData = null,
        } = await Api.enableSocietyMember(member.id);
        if (success) {
          this.alertMsg = {
            type: 'success',
            message: `Successfully enabled member ${member.email}`,
          };
          if (memberData) {
            const memberIndex = this.members.findIndex(
              (m) => m.id === memberData.id
            );
            if (memberIndex) this.$set(this.members, memberIndex, memberData);
          }
          this.enableLoading = false;
          this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
        }
      } catch (error) {
        this.alertMsg = {
          type: 'danger',
          message:
            error && error.response && error.response.data
              ? error.response.data.message
              : 'Server error',
        };
        this.enableLoading = false;
        this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
      }
    },
    onEnableDisable: async function (member) {
      Help.confirm.show(
        `Are you sure that you want to enable member ${member.email}?`,
        'Yes',
        () => {
          this.enableDisable(member);
        }
      );
    },
    onEditMember: function (member) {
      this.openEditModal(member);
    },
    openEditModal: function (member) {
      this.editedMember = member;
      this.showEditModal = true;
    },
    onModalCancel() {
      this.editedMember = null;
      this.showEditModal = false;
    },
    onUserSaved(user) {
      this.$set(
        this.editedMember,
        'status',
        user.disabled ? MEMBER_STATUS_DISABLED : MEMBER_STATUS_ACTIVE
      );
    },
    onShowLink: async function (member) {
      this.loadingRegTokenButtonId = member.id;
      this.registrationToken = await Api.getRegistrationToken(member.id);
      this.registrationTokenModalTitle = member.email;
      this.isRegistrationModalVisible = true;
      this.loadingRegTokenButtonId = null;
    },
    addMemberToList: function (member) {
      this.members.push(member);
    },

    onGenerateCSV: function () {
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += 'RowNum,ID,Email,Demo,SocietyID,ZipCode,Specialty,Subspecialty,PrimaryStudyStatus,Gender,MemberType,Registration\n';
      csvContent += this.filteredMembers
        .map((m, index) =>
          [
            index + 1,
            m.id,
            `"${csvEscape(m.email)}"`,
            `"${csvEscape(m.is_demo)}"`,
            `"${csvEscape(m.society_member_uid)}"`,
            `"${csvEscape(m.zip_code)}"`,
            `"${csvEscape(m.specialty)}"`,
            `"${csvEscape(m.subspecialty)}"`,
            `"${csvEscape(this.studyStatusToText(m.user))}"`,
            `"${csvEscape(this.optionsKeyToText(m.gender, 'genderOptions'))}"`,
            `"${csvEscape(m.member_type)}"`,
            `"${csvEscape(m.send_reg_email_date)}"`,
          ].join(',')
        )
        .join('\n');
      let encodedUri = encodeURI(csvContent);
      let link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'MemberList.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  },
};
</script>
